<template>
	<div class="trip-offer-tile" :class="{ checked: isChecked, available: isChecked && isAvailable, 'not-available': isChecked && !isAvailable }">
		<!-- Available -->
		<div class="available" v-if="isChecked && isAvailable">
			<i class="fa fa-check"></i> {{ 'tiles.offer.availableText' | trans }}
		</div>

		<!-- Not available -->
		<div class="not-available" v-if="isChecked && !isAvailable">
			<i class="fa fa-times"></i> {{ 'tiles.offer.notAvailableText' | trans }}
		</div>

		<div class="row">
			<div class="col-sm-6">
				<!-- Outbound flight -->
				<div class="flight outbound">
					<!-- Departure -->
					<div class="departure">
						<span class="time">
							{{ offer.flight.outbound.period.departure.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }) }}
						</span>
						<span class="airport">
							{{ offer.flight.outbound.departureAirport.name }}
							<code>{{ offer.flight.outbound.departureAirport.code }}</code>
						</span>
						<span class="date">
							{{ offer.flight.outbound.period.departure.toLocaleDateString('de-DE', { weekday: 'long', month: 'long', day: '2-digit' }) }}
						</span>
					</div>

					<!-- Info -->
					<div class="info">
						<i class="fa fa-plane"></i>
						<small>
							{{ parseInt(offer.flight.outbound.duration/60, 10) }}:{{ offer.flight.outbound.duration%60 }}
						</small>
						<small v-if="offer.flight.outbound.stopOver > 0">
							{{ offer.flight.outbound.stopOver }}
							<span v-if="offer.flight.outbound.stopOver === 1">
								{{ 'tiles.offer.flight.stop' | trans }}
							</span>
							<span v-if="offer.flight.outbound.stopOver > 1">
								{{ 'tiles.offer.flight.stops' | trans }}
							</span>
						</small>
					</div>

					<!-- Arrival -->
					<div class="arrival">
						<span class="time">
							{{ offer.flight.outbound.period.arrival.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }) }}
						</span>
						<span class="airport">
							{{ offer.flight.outbound.arrivalAirport.name }}
							<code>{{ offer.flight.outbound.arrivalAirport.code }}</code>
						</span>
						<span class="date">
							{{ offer.flight.outbound.period.arrival.toLocaleDateString('de-DE', { weekday: 'long', month: 'long', day: '2-digit' }) }}
						</span>
					</div>

					<!-- Airline -->
					<div class="airline">
						<span v-if="isOutboundAirlineLogo === false">
							{{ offer.flight.outbound.airline.name }}
						</span>
						<img :src="$cdn.default + 'airlines/' + offer.flight.outbound.airline.code.toLowerCase() + '.png?width=209'" @load="isOutboundAirlineLogo = true" @error="hideOutboundAirlineLogo()" v-show="isOutboundAirlineLogo" />
					</div>
				</div>

				<div class="divider"></div>

				<!-- Inbound flight -->
				<div class="flight inbound">
					<!-- Departure -->
					<div class="departure">
						<span class="time">
							{{ offer.flight.inbound.period.departure.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }) }}
						</span>
						<span class="airport">
							{{ offer.flight.inbound.departureAirport.name }}
							<code>{{ offer.flight.inbound.departureAirport.code }}</code>
						</span>
						<span class="date">
							{{ offer.flight.inbound.period.departure.toLocaleDateString('de-DE', { weekday: 'long', month: 'long', day: '2-digit' }) }}
						</span>
					</div>

					<!-- Info -->
					<div class="info">
						<i class="fa fa-plane"></i>
						<small>
							{{ parseInt(offer.flight.inbound.duration/60, 10) }}:{{ offer.flight.inbound.duration%60 }}
						</small>
						<small v-if="offer.flight.inbound.stopOver > 0">
							{{ offer.flight.inbound.stopOver }}
							<span v-if="offer.flight.inbound.stopOver === 1">
								{{ 'tiles.offer.flight.stop' | trans }}
							</span>
							<span v-if="offer.flight.inbound.stopOver > 1">
								{{ 'tiles.offer.flight.stops' | trans }}
							</span>
						</small>
					</div>

					<!-- Arrival -->
					<div class="arrival">
						<span class="time">
							{{ offer.flight.inbound.period.arrival.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }) }}
						</span>
						<span class="airport">
							{{ offer.flight.inbound.arrivalAirport.name }}
							<code>{{ offer.flight.inbound.arrivalAirport.code }}</code>
						</span>
						<span class="date">
							{{ offer.flight.inbound.period.arrival.toLocaleDateString('de-DE', { weekday: 'long', month: 'long', day: '2-digit' }) }}
						</span>
					</div>

					<!-- Airline -->
					<div class="airline">
						<span v-if="isInboundAirlineLogo === false">
							{{ offer.flight.inbound.airline.name }}
						</span>
						<img :src="$cdn.default + 'airlines/' + offer.flight.inbound.airline.code.toLowerCase() + '.png?width=209'" @load="isInboundAirlineLogo = true" @error="hideInboundAirlineLogo()" v-show="isInboundAirlineLogo" />
					</div>
				</div>
			</div>
			<div class="col-sm-3">
				<!-- Room -->
				<div class="room">
					<label>{{ 'tiles.offer.room' | trans }}</label>
					<span>{{ offer.room.name }}</span>
				</div>

				<!-- Included services -->
				<div class="included-services">
					<span>{{ 'tiles.offer.includedServices' | trans }}</span>

					<ul class="services included">
						<li>
							{{ offer.lengthOfStay }}
							<span v-if="offer.lengthOfStay === 1">{{ 'tiles.offer.night' | trans }}</span>
							<span v-if="offer.lengthOfStay > 1">{{ 'tiles.offer.nights' | trans }}</span>
						</li>
						<li v-if="offer.board.name.length > 0">{{ offer.board.name }}</li>
						<li v-for="extra in offer.extras" v-if="extra.included === true">
							<!-- With subtype -->
							<span v-if="extra.subtype.name !== null">
								{{ extra.subtype.name }} <i class="fa fa-info-circle" :title="extra.subtype.description"></i>
								<small>{{ extra.name }}</small>
							</span>
							
							<!-- Without subtype -->
							<span v-if="extra.subtype.name === null">
								{{ extra.name }} <i class="fa fa-info-circle" :title="extra.description"></i>
							</span>
						</li>
					</ul>
				</div>
			</div>
			<div class="col-sm-3">
				<!-- Tour operator -->
				<div class="tour-operator">
					<span v-if="isTourOperatorLogo === false">
						{{ offer.tourOperator.name }}
					</span>

					<img :src="$cdn.default + 'tour-operators/' + offer.tourOperator.code.toLowerCase() + '?width=300'" @load="isTourOperatorLogo = true" v-show="isTourOperatorLogo" @error="hideTourOperatorLogo()" />
				</div>

				<!-- Persons -->
				<span class="persons">
					{{ $root.$refs.filterbar.adults + $root.$refs.filterbar.children.length }} {{ 'nav.filterbar.persons' | trans }}
				</span>
				
				<div class="bottom">
					<!-- From price -->
					<div class="from-price">
						<span class="per-person" v-if="offer.price.perPerson === true">
							{{ offer.price.amount | formatPrice }}
							<small>{{ 'tiles.offer.perPerson' | trans }} &euro;</small>
						</span>

						<span class="total">
							<strong>{{ offer.price.totalAmount | formatPrice }} &euro;</strong> {{ 'tiles.offer.totalPrice' | trans }}
						</span>
					</div>

					<!-- Buttons -->
					<div class="buttons">
						<button type="button" class="btn btn-primary btn-lg" v-if="!isChecked" @click="checkOffer()">
							<span v-if="!isChecking">
								{{ 'tiles.offer.checkOffer' | trans }}
							</span>
							<span v-if="isChecking">
								<i class="fa fa-spinner fa-spin"></i>
							</span>
						</button>

						<button type="button" class="btn btn-primary btn-lg" v-if="isChecked && isAvailable">
							{{ 'tiles.offer.goToBooking' | trans }}
						</button>

						<span class="not-available" v-if="isChecked && !isAvailable">
							{{ 'tiles.offer.notAvailableText' | trans }}
						</span>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
export default {
	props: ['offer'],
	data: function() {
		return {
			isOutboundAirlineLogo: false,
			isInboundAirlineLogo: false,
			isTourOperatorLogo: false,
			isChecked: false,
			isChecking: false,
			isAvailable: false
		}
	},
	mounted: function() {},
	computed: {},
	methods: {
		hideOutboundAirlineLogo: function() {
			this.isOutboundAirlineLogo = false;
		},
		hideInboundAirlineLogo: function() {
			this.isInboundAirlineLogo = false;
		},
		hideTourOperatorLogo: function() {
			this.isTourOperatorLogo = false;
		},
		checkOffer: function() {
			this.isChecking = true;

			var self = this;
			setTimeout(function() {
				var random = Math.floor(Math.random() * 2);
				if (random === 1) {
					self.isChecked = true;
					self.isAvailable = true;
				} else {
					self.isChecked = true;
					self.isAvailable = false;
				}

				self.isChecking = false;
			}, 2000);
		},
		loadFlightAlternatives: function() {
			console.log('Lade alternative Flüge.');
		}
	}
}
</script>